import { useId, type ReactElement, type ReactNode } from "react";
import { ArrowLeft, Code2, ExternalLink, GitFork, Image, Layers3, Music2, Play, Plus, Replace, Trash2, UserRound } from "lucide-react";
import { Button, Textarea, TextInput } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";
import type { InsertKind } from "../editor/block-templates.js";
import type { PresentationStep, StoryboardBranch } from "./presentation-model.js";
import type { PresentationStepFields } from "./presentation-source.js";

const S = {
  editStep: { zh: "编辑当前步骤", en: "Edit this step" },
  editChoice: { zh: "编辑玩家选择", en: "Edit player choice" },
  editOptionTitle: { zh: "编辑选项 {id}", en: "Edit option {id}" },
  editGate: { zh: "编辑本集出口", en: "Edit episode exit" },
  editRouteRule: { zh: "编辑出口规则", en: "Edit exit rule" },
  line: { zh: "第 {line} 行", en: "Line {line}" },
  advancedStep: { zh: "高级步骤", en: "Advanced step" },
  advancedHint: {
    zh: "这个步骤包含结构或自定义语法。为避免破坏剧情逻辑，请在代码模式中编辑。",
    en: "This step contains structure or custom syntax. Edit it in Code to preserve the story logic.",
  },
  source: { zh: "原始源码", en: "Source" },
  editCode: { zh: "在代码中编辑", en: "Edit in Code" },
  playtest: { zh: "打开完整试玩", en: "Open Playtest" },
  openAssets: { zh: "打开素材", en: "Open Assets" },
  branchKey: { zh: "章节标识", en: "Branch key" },
  title: { zh: "章节标题", en: "Episode title" },
  background: { zh: "背景素材", en: "Background asset" },
  backgroundHint: { zh: "填写素材库中的背景名称，例如 cafe_evening。", en: "Use a background name from Assets, for example cafe_evening." },
  transition: { zh: "转场", en: "Transition" },
  transitionHint: { zh: "画面如何切换，例如 dissolve（溶解）或 fade（淡入）。", en: "How the picture changes, for example dissolve or fade." },
  character: { zh: "角色", en: "Character" },
  characterHint: { zh: "填写角色标识，例如 LUNA。", en: "Use the character identifier, for example LUNA." },
  look: { zh: "表情 / 造型", en: "Look" },
  lookHint: { zh: "填写角色造型或表情素材名，例如 soft_smile。", en: "Enter the character look or expression asset, for example soft_smile." },
  presentation: { zh: "呈现方式", en: "Presentation" },
  presentationHint: { zh: "选择显示角色立绘或情绪气泡。", en: "Choose whether to show a character sprite or an emotion bubble." },
  sprite: { zh: "角色立绘", en: "Character sprite" },
  bubble: { zh: "情绪气泡", en: "Emotion bubble" },
  speaker: { zh: "说话角色", en: "Speaker" },
  speakerHint: { zh: "填写说话角色标识，例如 LUNA。", en: "Enter the speaking character identifier, for example LUNA." },
  dialogue: { zh: "台词", en: "Dialogue" },
  dialogueHint: { zh: "角色会在对话框中说出这句话。", en: "The character says this line in the dialogue box." },
  narrationMode: { zh: "叙述方式", en: "Narration mode" },
  narrator: { zh: "旁白", en: "Narrator" },
  innerThought: { zh: "内心想法", en: "Inner thought" },
  content: { zh: "内容", en: "Content" },
  narrationHint: { zh: "这段文字会作为旁白或内心想法出现在画面中。", en: "This text appears as narration or an inner thought." },
  assetName: { zh: "素材名称", en: "Asset name" },
  cgAssetHint: { zh: "填写 CG 素材名，例如 reunion_closeup。", en: "Enter the CG asset name, for example reunion_closeup." },
  cgDescriptionHint: { zh: "描述关键画面的构图与情绪，供素材生成和团队理解使用。", en: "Describe the key visual's composition and mood for asset generation and collaborators." },
  musicAction: { zh: "音乐状态", en: "Music state" },
  musicActionHint: { zh: "选择开始播放音乐或停止当前音乐。", en: "Choose whether to start a track or stop the current music." },
  musicAssetHint: { zh: "填写音乐素材名，例如 reunion_theme。", en: "Enter the music asset name, for example reunion_theme." },
  sfxAssetHint: { zh: "填写音效素材名，例如 door_open。", en: "Enter the sound-effect asset name, for example door_open." },
  playMusic: { zh: "播放", en: "Play" },
  stopMusic: { zh: "停止", en: "Stop" },
  optionId: { zh: "选项 ID", en: "Option ID" },
  optionIdHint: { zh: "用于剧情判断和出口规则；请使用简短且唯一的名称，例如 A 或 ASK_RIN。", en: "Used by story conditions and exit rules; choose a short unique name, for example A or ASK_RIN." },
  optionGroup: { zh: "选项 {id}", en: "Option {id}" },
  behavior: { zh: "选择方式", en: "Behavior" },
  choiceModeHint: { zh: "稳妥选项会直接继续；勇敢选项会先进行检定，再按结果展开剧情。", en: "Safe continues directly; Brave checks first, then follows the result." },
  choiceModeCheckLockedHint: {
    zh: "这个选项包含成功/失败检定剧情。请先进入“选择后发生什么”移除或重构检定分支，再改为稳妥。",
    en: "This option contains success/failure check branches. Open “What happens after this option” and remove or restructure them before changing to Safe.",
  },
  brave: { zh: "勇敢：先检定，再按结果继续", en: "Brave (with check)" },
  safe: { zh: "稳妥：不检定，直接继续", en: "Safe" },
  buttonLabel: { zh: "按钮上显示的文字", en: "Button label" },
  buttonLabelHint: { zh: "玩家会在画面中看到这句话。", en: "Players see this sentence on screen." },
  amount: { zh: "变化值", en: "Amount" },
  signalType: { zh: "状态类型", en: "Signal type" },
  signalTypeHint: { zh: "事件标记记录是否发生；数值状态保存可加减的数值。", en: "Event marks record whether something happened; number states store a value that can change." },
  stateName: { zh: "状态名称", en: "State name" },
  stateNameHint: { zh: "填写供剧情条件引用的状态名，例如 trust。", en: "Enter the state name used by story conditions, for example trust." },
  mark: { zh: "事件标记", en: "Event mark" },
  integer: { zh: "数值状态", en: "Number state" },
  operation: { zh: "运算", en: "Operation" },
  operationHint: { zh: "设置数值如何变化，例如 + 或 =。", en: "Choose how the number changes, for example + or =." },
  value: { zh: "值", en: "Value" },
  valueHint: { zh: "填写变化或设置的数值，例如 1。", en: "Enter the value to add or set, for example 1." },
  interactionType: { zh: "互动类型", en: "Interaction type" },
  interactionTypeHint: { zh: "填写互动模板，例如 tap 或 hold。", en: "Enter the interaction template, for example tap or hold." },
  prompt: { zh: "操作提示", en: "Prompt" },
  direction: { zh: "消息方向", en: "Message direction" },
  from: { zh: "来自角色", en: "From character" },
  to: { zh: "发给角色", en: "To character" },
  description: { zh: "说明", en: "Description" },
  parameter: { zh: "参数名", en: "Parameter" },
  routeAction: { zh: "出口行为", en: "Route action" },
  nextEpisode: { zh: "进入下一章", en: "Go to next episode" },
  endStory: { zh: "结束剧情", en: "End story" },
  routeTarget: { zh: "目标章节 / 结局标识", en: "Episode / ending target" },
  routeTargetHint: { zh: "填写下一集或结局标识，例如 main:02 或 alone。", en: "Enter the next episode or ending identifier, for example main:02 or alone." },
  routeRules: { zh: "出口规则", en: "Exit rules" },
  routeRuleHint: { zh: "点击一条规则修改条件和目标。", en: "Select a rule to edit its condition and target." },
  condition: { zh: "触发条件", en: "Condition" },
  conditionHint: { zh: "例如 affection.rin >= 2；条件成立时执行这条出口规则。", en: "For example affection.rin >= 2; this exit runs when the condition is true." },
  pauseHint: { zh: "玩家点击后，剧情会继续到下一步。", en: "The story continues after the player taps." },
  frameType: { zh: "帧模板", en: "Frame template" },
  replaceType: { zh: "替换当前帧类型", en: "Replace current frame type" },
  replaceHint: { zh: "会保留位置，并换成所选模板的可编辑内容。", en: "Keeps this position and replaces its editable content with the chosen template." },
  scene: { zh: "场景", en: "Scene" },
  dialogueType: { zh: "对白", en: "Dialogue" },
  narrationType: { zh: "旁白", en: "Narration" },
  keyMoment: { zh: "关键画面", en: "Key moment" },
  sameTime: { zh: "与上一帧同时发生", en: "Happens at the same time as the previous frame" },
  sameTimeHint: { zh: "适合让音乐、人物和背景一起出现；源码会使用 & 并发语法。", en: "Use this for music, character, and background staging that should appear together. This writes the & concurrency form." },
  visibleTogether: { zh: "同一画面还可以加入", en: "Also show in this frame" },
  visibleNow: { zh: "同一画面已经包含", en: "Already in this frame" },
  addMusic: { zh: "背景音乐", en: "Background music" },
  addCharacter: { zh: "人物 / 表情", en: "Character / look" },
  choiceGuide: { zh: "玩家会从下面的按钮中选择接下来怎么做。", en: "Players choose what to do next from the buttons below." },
  choiceOptions: { zh: "这个选择的选项", en: "Options in this choice" },
  editOption: { zh: "编辑选项", en: "Edit option" },
  addOption: { zh: "添加一个选项", en: "Add an option" },
  removeNamedOption: { zh: "删除选项 {id}", en: "Remove option {id}" },
  keepTwo: { zh: "至少保留两个选项，玩家才能做出选择。", en: "Keep at least two options so the player has a meaningful choice." },
  optionContent: { zh: "选择后发生什么", en: "What happens after this option" },
  editContent: { zh: "点击编辑", en: "Select to edit" },
  optionOutcomes: { zh: "选项结果", en: "Option outcomes" },
  noOutcome: { zh: "选择后继续剧情", en: "Story continues after selection" },
  episodeExits: { zh: "本集之后去哪里", en: "Episode exits" },
  episodeExitsHint: { zh: "剧情执行完选项内容后再判断，不是选项直接跳转。", en: "These rules run after the option content finishes; an option does not jump here directly." },
  whenCondition: { zh: "当 {condition}", en: "if {condition}" },
  otherwise: { zh: "其他情况", en: "otherwise" },
  nextTarget: { zh: "进入 {target}", en: "Go to {target}" },
  endTarget: { zh: "结束于 {target}", en: "End as {target}" },
  editExit: { zh: "编辑出口", en: "Edit exit" },
  returnToOptions: { zh: "返回选项列表", en: "Back to options" },
  technicalSettings: { zh: "技术设置", en: "Technical settings" },
  sourceHint: { zh: "视觉编辑会实时写回这里。", en: "Visual edits are written here immediately." },
} satisfies Record<string, Entry>;

interface SelectFieldProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly hint?: ReactNode;
  readonly className?: string;
}

function SelectField({ id, label, value, onChange, children, disabled, hint, className }: SelectFieldProps): ReactElement {
  const hintId = `${id}-hint`;
  return (
    <div className={["studio-select-field", className].filter(Boolean).join(" ")}>
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} disabled={disabled} aria-describedby={hint != null ? hintId : undefined} onChange={(event) => onChange(event.currentTarget.value)}>{children}</select>
      {hint != null ? <small id={hintId}>{hint}</small> : null}
    </div>
  );
}

export interface StepInspectorProps {
  readonly step: PresentationStep | null;
  readonly onPatch: (fields: PresentationStepFields) => void;
  readonly onPatchStep: (line: number, fields: PresentationStepFields) => void;
  readonly onOpenCode: (line: number) => void;
  readonly onOpenAssets: () => void;
  readonly onOpenPlaytest: () => void;
  readonly onReplace?: (kind: InsertKind) => void;
  readonly onConcurrentChange?: (concurrent: boolean) => void;
  readonly onAddCompanion?: (kind: InsertKind) => void;
  readonly choiceBranch?: StoryboardBranch;
  readonly contextChoiceLine?: number;
  readonly optionContents?: readonly PresentationStep[];
  readonly gateContents?: readonly PresentationStep[];
  readonly companions?: readonly PresentationStep[];
  readonly canMakeConcurrent?: boolean;
  readonly onSelectStep?: (line: number) => void;
  readonly onAddChoiceOption?: () => void;
  readonly onDeleteChoiceOption?: (line: number) => void;
  readonly assetAssistant?: ReactNode;
  readonly disabled?: boolean;
}

export function StepInspector({
  step,
  onPatch,
  onPatchStep,
  onOpenCode,
  onOpenAssets,
  onOpenPlaytest,
  onReplace,
  onConcurrentChange,
  onAddCompanion,
  choiceBranch,
  contextChoiceLine,
  optionContents = [],
  gateContents = [],
  companions = [],
  canMakeConcurrent = true,
  onSelectStep,
  onAddChoiceOption,
  onDeleteChoiceOption,
  assetAssistant,
  disabled,
}: StepInspectorProps): ReactElement {
  const tr = useTr();
  const uid = useId().replace(/:/g, "");

  if (!step) {
    return (
      <div className="studio-inspector studio-inspector--empty">
        <strong>{tr(S.editStep)}</strong>
        <span>{tr(S.pauseHint)}</span>
      </div>
    );
  }

  const block = step.block;
  const contextOption = block.kind === "option"
    ? choiceBranch?.options.find((option) => option.line === step.line)
    : undefined;
  const nestedSafeLocked = contextOption?.hasCheckDependentOutcomes ?? false;
  const input = (field: keyof PresentationStepFields, label: Entry, value: string | undefined, placeholder?: string, hint?: Entry) => (
    <TextInput
      key={field}
      label={tr(label)}
      hint={hint ? tr(hint) : undefined}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => onPatch({ [field]: event.currentTarget.value })}
    />
  );
  const textarea = (field: keyof PresentationStepFields, label: Entry, value: string | undefined, hint?: Entry) => (
    <Textarea
      key={field}
      label={tr(label)}
      hint={hint ? tr(hint) : undefined}
      value={value ?? ""}
      rows={5}
      disabled={disabled}
      onChange={(event) => onPatch({ [field]: event.currentTarget.value })}
    />
  );
  const assetAction = (
    <Button variant="ghost" size="sm" leadingIcon={<Image size={15} />} onClick={onOpenAssets}>{tr(S.openAssets)}</Button>
  );
  const localizeOutcome = (outcome: string) => outcome
    .replace(/^if ([^→]+)(?= →)/, (_, condition: string) => tr(S.whenCondition, { condition: condition.trim() }))
    .replace(/^otherwise(?= →)/, tr(S.otherwise));

  let fields: ReactNode = null;
  switch (block.kind) {
    case "episode":
      fields = <>{input("branchKey", S.branchKey, block.branchKey, "main:01")}{input("title", S.title, block.title)}</>;
      break;
    case "bg":
      fields = <>
        {input("name", S.background, block.name, "cafe_evening", S.backgroundHint)}{input("transition", S.transition, block.transition, "dissolve", S.transitionHint)}{assetAction}
        {companions.length ? (
          <div className="studio-inspector__current-companions">
            <span>{tr(S.visibleNow)}</span>
            {companions.map((companion) => (
              <button type="button" key={companion.id} onClick={() => onSelectStep?.(companion.line)}>
                {companion.kind === "music" ? <Music2 size={15} /> : companion.kind === "char" ? <UserRound size={15} /> : <Layers3 size={15} />}
                <strong>{tr(companion.label)}</strong><small>{companion.summary}</small><em>{tr(S.editOption)}</em>
              </button>
            ))}
          </div>
        ) : null}
        {onAddCompanion ? (
          <div className="studio-inspector__companions">
            <span>{tr(S.visibleTogether)}</span>
            <Button variant="secondary" size="sm" leadingIcon={<Music2 size={15} />} onClick={() => onAddCompanion("music")}>{tr(S.addMusic)}</Button>
            <Button variant="secondary" size="sm" leadingIcon={<UserRound size={15} />} onClick={() => onAddCompanion("char")}>{tr(S.addCharacter)}</Button>
          </div>
        ) : null}
      </>;
      break;
    case "char":
      fields = <>
        {input("character", S.character, block.character, "LUNA", S.characterHint)}
        <SelectField id={`${uid}-presentation`} label={tr(S.presentation)} hint={tr(S.presentationHint)} value={block.bubble ? "bubble" : "sprite"} disabled={disabled} onChange={(value) => onPatch({ bubble: value === "bubble" })}>
          <option value="sprite">{tr(S.sprite)}</option><option value="bubble">{tr(S.bubble)}</option>
        </SelectField>
        {input("pose", block.bubble ? S.bubble : S.look, block.pose, block.bubble ? "heart" : "soft_smile", S.lookHint)}
        {!block.bubble ? input("transition", S.transition, block.transition, "fade", S.transitionHint) : null}
        {assetAction}
      </>;
      break;
    case "dialogue":
      fields = <>{input("speaker", S.speaker, block.speaker, "LUNA", S.speakerHint)}{input("pose", S.look, block.pose, "soft_smile", S.lookHint)}{textarea("text", S.dialogue, block.text, S.dialogueHint)}</>;
      break;
    case "narration":
      fields = <>
        <SelectField id={`${uid}-narration`} label={tr(S.narrationMode)} value={block.speaker} disabled={disabled} onChange={(speaker) => onPatch({ speaker })}>
          <option value="NARRATOR">{tr(S.narrator)}</option><option value="YOU">{tr(S.innerThought)}</option>
        </SelectField>
        {textarea("text", S.content, block.text, S.narrationHint)}
      </>;
      break;
    case "cg":
      fields = <>{input("name", S.assetName, block.name, "reunion_closeup", S.cgAssetHint)}{textarea("content", S.description, block.content, S.cgDescriptionHint)}{assetAction}</>;
      break;
    case "music":
      fields = <>
        <SelectField id={`${uid}-music`} label={tr(S.musicAction)} hint={tr(S.musicActionHint)} value={block.stop ? "stop" : "play"} disabled={disabled} onChange={(value) => onPatch({ stop: value === "stop" })}>
          <option value="play">{tr(S.playMusic)}</option><option value="stop">{tr(S.stopMusic)}</option>
        </SelectField>
        {!block.stop ? input("name", S.assetName, block.name, "reunion_theme", S.musicAssetHint) : null}{assetAction}
      </>;
      break;
    case "sfx":
      fields = <>{input("name", S.assetName, block.name, "door_open", S.sfxAssetHint)}{assetAction}</>;
      break;
    case "pause":
      fields = <p className="studio-inspector__hint">{tr(S.pauseHint)}</p>;
      break;
    case "choice":
      fields = <div className="studio-choice-editor">
        <div className="studio-choice-editor__guide"><GitFork size={20} /><p>{tr(S.choiceGuide)}</p></div>
        <strong>{tr(S.choiceOptions)}</strong>
        <div className="studio-choice-editor__options">
          {choiceBranch?.options.map((option) => (
            <fieldset className="studio-choice-editor__option" data-option-id={option.id} key={option.line}>
              <legend className="studio-choice-editor__option-legend">
                <span className="studio-choice-editor__option-badge" aria-hidden="true">{option.id}</span>
                <span>{tr(S.optionGroup, { id: option.id })}</span>
              </legend>
              <div className="studio-choice-editor__option-card">
                <TextInput
                  id={`${uid}-choice-${option.line}-text`}
                  className="studio-choice-editor__option-text"
                  label={tr(S.buttonLabel)}
                  hint={tr(S.buttonLabelHint)}
                  value={option.text}
                  disabled={disabled}
                  onChange={(event) => onPatchStep(option.line, { text: event.currentTarget.value })}
                />
                <SelectField
                  id={`${uid}-choice-${option.line}-mode`}
                  className="studio-choice-editor__option-mode"
                  label={tr(S.behavior)}
                  hint={tr(option.hasCheckDependentOutcomes ? S.choiceModeCheckLockedHint : S.choiceModeHint)}
                  value={option.mode}
                  disabled={disabled}
                  onChange={(mode) => {
                    if (mode === "safe" && option.hasCheckDependentOutcomes) return;
                    onPatchStep(option.line, { mode: mode as "brave" | "safe" });
                  }}
                >
                  <option value="brave">{tr(S.brave)}</option><option value="safe" disabled={option.hasCheckDependentOutcomes}>{tr(S.safe)}</option>
                </SelectField>
                <div className="studio-option-content studio-choice-editor__option-outcomes" aria-label={`${tr(S.optionOutcomes)} ${option.id}`}>
                  <strong>{tr(S.optionOutcomes)}</strong>
                  {option.outcomes.length > 0
                    ? option.outcomes.map((outcome) => <small key={outcome}>{localizeOutcome(outcome)}</small>)
                    : <small>{tr(S.noOutcome)}</small>}
                  <button type="button" onClick={() => onSelectStep?.(option.line)}>
                    <span>{option.id}</span><small>{tr(S.optionContent)}</small><em>{tr(S.editContent)}</em>
                  </button>
                </div>
              </div>
              {(choiceBranch?.options.length ?? 0) > 2 && onDeleteChoiceOption ? <button type="button" className="studio-choice-editor__delete" aria-label={tr(S.removeNamedOption, { id: option.id })} title={tr(S.removeNamedOption, { id: option.id })} disabled={disabled} onClick={() => onDeleteChoiceOption(option.line)}><Trash2 size={13} /></button> : null}
            </fieldset>
          ))}
        </div>
        {onAddChoiceOption ? <Button variant="secondary" size="sm" leadingIcon={<Plus size={14} />} disabled={disabled} onClick={onAddChoiceOption}>{tr(S.addOption)}</Button> : null}
        <small className="studio-choice-editor__keep">{tr(S.keepTwo)}</small>
        {choiceBranch?.exits.length ? <div className="studio-option-content studio-choice-editor__exits" aria-label={tr(S.episodeExits)}>
          <strong>{tr(S.episodeExits)}</strong>
          <p>{tr(S.episodeExitsHint)}</p>
          {choiceBranch.exits.map((exit) => (
            <button type="button" key={exit.line} onClick={() => onSelectStep?.(exit.line)}>
              <span>{exit.condition ? tr(S.whenCondition, { condition: exit.condition }) : tr(S.otherwise)}</span>
              <small>{tr(exit.action === "next" ? S.nextTarget : S.endTarget, { target: exit.target })}</small>
              <em>{tr(S.editExit)}</em>
            </button>
          ))}
        </div> : null}
      </div>;
      break;
    case "option":
      fields = <>
        {textarea("text", S.buttonLabel, block.text, S.buttonLabelHint)}
        <SelectField id={`${uid}-option`} label={tr(S.behavior)} hint={tr(nestedSafeLocked ? S.choiceModeCheckLockedHint : S.choiceModeHint)} value={block.mode ?? "safe"} disabled={disabled} onChange={(mode) => {
          if (mode === "safe" && nestedSafeLocked) return;
          onPatch({ mode: mode as "brave" | "safe" });
        }}>
          <option value="brave">{tr(S.brave)}</option><option value="safe" disabled={nestedSafeLocked}>{tr(S.safe)}</option>
        </SelectField>
        {optionContents.length ? <div className="studio-option-content">
          <strong>{tr(S.optionContent)}</strong>
          {optionContents.map((content) => (
            <button type="button" key={content.id} style={{ marginInlineStart: `${Math.max(0, content.depth - step.depth - 1) * 10}px` }} onClick={() => onSelectStep?.(content.line)}>
              <span>{tr(content.label)}</span><small>{content.summary}</small><em>{tr(S.editContent)}</em>
            </button>
          ))}
        </div> : null}
        <details className="studio-inspector__technical">
          <summary>{tr(S.technicalSettings)}</summary>
          {input("id", S.optionId, block.id, "A", S.optionIdHint)}
        </details>
      </>;
      break;
    case "affection":
      fields = <>{input("character", S.character, block.character, "luna")}{input("delta", S.amount, block.delta, "+1")}</>;
      break;
    case "signal":
      fields = <>
        <SelectField id={`${uid}-signal`} label={tr(S.signalType)} hint={tr(S.signalTypeHint)} value={block.signalKind ?? "mark"} disabled={disabled} onChange={(signalKind) => onPatch({ signalKind: signalKind as "mark" | "int" })}>
          <option value="mark">{tr(S.mark)}</option><option value="int">{tr(S.integer)}</option>
        </SelectField>
        {input("name", S.stateName, block.name, "trust", S.stateNameHint)}
        {block.signalKind === "int" ? <>{input("op", S.operation, block.op, "+", S.operationHint)}{input("value", S.value, block.value, "1", S.valueHint)}</> : null}
      </>;
      break;
    case "trick":
      fields = <>{input("trickType", S.interactionType, block.trickType, "tap", S.interactionTypeHint)}{textarea("prompt", S.prompt, block.prompt)}</>;
      break;
    case "phoneText":
      fields = <>
        <SelectField id={`${uid}-direction`} label={tr(S.direction)} value={block.direction ?? "from"} disabled={disabled} onChange={(direction) => onPatch({ direction: direction as "from" | "to" })}>
          <option value="from">{tr(S.from)}</option><option value="to">{tr(S.to)}</option>
        </SelectField>
        {input("character", S.character, block.character, "LUNA")}{textarea("text", S.content, block.text)}
      </>;
      break;
    case "minigame":
      fields = <>{input("name", S.title, block.name, "color-match")}{textarea("description", S.description, block.description)}</>;
      break;
    case "butterfly":
      fields = textarea("description", S.description, block.description);
      break;
    case "param":
      fields = <>{input("key", S.parameter, block.key)}{input("value", S.value, block.value)}</>;
      break;
    case "gate":
      fields = block.raw.includes("}") ? <>
        <SelectField id={`${uid}-route`} label={tr(S.routeAction)} value={block.next ? "next" : "end"} disabled={disabled} onChange={(mode) => onPatch(mode === "next" ? { next: block.next || "next:01", end: undefined } : { next: undefined, end: block.end || "to_be_continued" })}>
          <option value="next">{tr(S.nextEpisode)}</option><option value="end">{tr(S.endStory)}</option>
        </SelectField>
        {block.next ? input("next", S.routeTarget, block.next, "next:01", S.routeTargetHint) : input("end", S.routeTarget, block.end, "to_be_continued", S.routeTargetHint)}
      </> : gateContents.length ? <div className="studio-option-content">
        <strong>{tr(S.routeRules)}</strong>
        <p className="studio-inspector__hint">{tr(S.routeRuleHint)}</p>
        {gateContents.map((content) => <button type="button" key={content.id} onClick={() => onSelectStep?.(content.line)}><span>{tr(content.label)}</span><small>{content.summary}</small><em>{tr(S.editContent)}</em></button>)}
      </div> : null;
      break;
    case "if":
      fields = block.next || block.end ? <>
        {block.condition != null ? input("condition", S.condition, block.condition, "affection.rin >= 2", S.conditionHint) : null}
        <SelectField id={`${uid}-route-rule`} label={tr(S.routeAction)} value={block.next ? "next" : "end"} disabled={disabled} onChange={(mode) => onPatch(mode === "next" ? { next: block.next || "next:01", end: undefined } : { next: undefined, end: block.end || "to_be_continued" })}>
          <option value="next">{tr(S.nextEpisode)}</option><option value="end">{tr(S.endStory)}</option>
        </SelectField>
        {block.next ? input("next", S.routeTarget, block.next, "main:02", S.routeTargetHint) : input("end", S.routeTarget, block.end, "alone", S.routeTargetHint)}
      </> : null;
      break;
    default:
      fields = null;
  }

  const advancedOnly = fields === null;
  const nestedChoiceEditor = contextChoiceLine != null && choiceBranch != null && (
    choiceBranch.options.some((option) => option.line === step.line)
    || choiceBranch.exits.some((exit) => exit.line === step.line)
  );
  const taskTitle = block.kind === "choice"
    ? tr(S.editChoice)
    : block.kind === "option"
      ? tr(S.editOptionTitle, { id: block.id ?? "" })
      : block.kind === "gate"
        ? tr(S.editGate)
        : block.kind === "if" && (block.next || block.end)
          ? tr(S.editRouteRule)
          : advancedOnly
            ? tr(S.advancedStep)
            : step.summary;
  const concurrentCapable = ["bg", "char", "cg", "music", "sfx", "pause", "affection", "signal", "trick", "phoneText", "minigame", "butterfly"].includes(step.kind);
  const replaceable = !step.concurrent && ["bg", "char", "dialogue", "narration", "cg", "music", "sfx", "pause", "affection", "signal", "trick", "phoneText", "minigame", "butterfly"].includes(step.kind);
  return (
    <section className="studio-inspector" aria-label={tr(S.editStep)}>
      {nestedChoiceEditor && onSelectStep ? (
        <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft size={14} />} onClick={() => onSelectStep(contextChoiceLine)}>{tr(S.returnToOptions)}</Button>
      ) : null}
      <header className="studio-inspector__head">
        <div>
          <span className="studio-inspector__eyebrow">{tr(step.label)}</span>
          <h2>{taskTitle}</h2>
        </div>
        <Button variant="ghost" size="sm" leadingIcon={<Play size={15} />} onClick={onOpenPlaytest}>{tr(S.playtest)}</Button>
      </header>

      {replaceable && onReplace ? (
        <div className="studio-inspector__type-row">
          <div><Replace size={16} /><span><strong>{tr(S.frameType)}</strong><small>{tr(S.replaceHint)}</small></span></div>
          <select aria-label={tr(S.replaceType)} defaultValue="" disabled={disabled} onChange={(event) => { const kind = event.currentTarget.value as InsertKind; if (kind) onReplace(kind); event.currentTarget.value = ""; }}>
            <option value="" disabled>{tr(S.replaceType)}</option>
            <option value="bg">{tr(S.scene)}</option>
            <option value="dialogue">{tr(S.dialogueType)}</option>
            <option value="narration">{tr(S.narrationType)}</option>
            <option value="cg">{tr(S.keyMoment)}</option>
          </select>
        </div>
      ) : null}

      {advancedOnly ? (
        <div className="studio-inspector__advanced-card">
          <Layers3 size={22} aria-hidden="true" />
          <div><strong>{tr(S.advancedStep)}</strong><p>{tr(S.advancedHint)}</p></div>
        </div>
      ) : (
        <div className="studio-inspector__form">{fields}</div>
      )}

      {assetAssistant}

      {concurrentCapable && onConcurrentChange && (step.concurrent || canMakeConcurrent) ? (
        <label className="studio-inspector__concurrent">
          <input type="checkbox" checked={step.concurrent} disabled={disabled} onChange={(event) => onConcurrentChange(event.currentTarget.checked)} />
          <span><strong>{tr(S.sameTime)}</strong><small>{tr(S.sameTimeHint)}</small></span>
        </label>
      ) : null}

      <div className="studio-inspector__source">
        <div><span><Code2 size={14} />{tr(S.source)}</span><small>{tr(S.line, { line: step.line })} · {tr(S.sourceHint)}</small></div>
        <pre>{block.raw}</pre>
        <Button variant="ghost" size="sm" leadingIcon={<ExternalLink size={14} />} onClick={() => onOpenCode(step.line)}>{tr(S.editCode)}</Button>
      </div>
    </section>
  );
}
